import { BIDDING_STRATEGY_OPTIONS, OPTIMIZATION_STRATEGY_OPTIONS } from '@/constants';
import { dayjs, formatInTimezone, getBrowserTimezone } from '@/utils/timezone';

export const formatCurrency = (value, currency = 'USD') => {
  if (!value && value !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
};

export const formatRevenue = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0';
  }
  const numValue = Number(value);
  return `$${numValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

export const formatRevPAF = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0.00';
  }
  const numValue = Number(value);
  return `$${numValue.toFixed(2)}`;
};

export const formatPercent = (value, decimals = 1) => {
  if (!value && value !== 0) return '0%';
  return `${(value * 100).toFixed(decimals)}%`;
};

export const formatOccupancy = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00%';
  }
  const numValue = Number(value);
  return `${numValue.toFixed(2)}%`;
};

/**
 * Format a date in the specified timezone
 * @param {string|Date|dayjs.Dayjs} date - The date to format
 * @param {string} tz - The timezone to use (defaults to browser timezone)
 * @returns {string} The formatted date string
 */
export const formatDate = (date, tz = getBrowserTimezone()) => {
  if (!date) return '';
  return formatInTimezone(date, 'MMM DD, YYYY', tz);
};

/**
 * Format a date with time in the specified timezone
 * @param {string|Date|dayjs.Dayjs} date - The date to format
 * @param {string} tz - The timezone to use (defaults to browser timezone)
 * @returns {string} The formatted datetime string
 */
export const formatDateTime = (date, tz = getBrowserTimezone()) => {
  if (!date) return '-';
  return formatInTimezone(date, 'MM/DD/YYYY hh:mm A', tz);
};

export const getChangeType = (apiData) => {
  // If percentage_change is 0 or not available, return neutral
  const percentageChange = apiData?.percentage_change;
  if (percentageChange === null || percentageChange === undefined || percentageChange === 0) {
    return 'neutral';
  }
  if (apiData?.increased === true) return 'positive';
  if (apiData?.increased === false) return 'negative';
  return 'neutral';
};

export const formatChange = (apiData, isPercentage = false) => {
  if (
    apiData?.percentage_change !== null &&
    apiData?.percentage_change !== undefined &&
    !isNaN(apiData.percentage_change)
  ) {
    const numValue = Number(apiData.percentage_change);
    const value = numValue.toFixed(2);
    return isPercentage ? `${value}%` : `${value}% MoM`;
  }
  return isPercentage ? '0.00%' : '0.00% MoM';
};

/**
 * Get formatted date from API data with timezone support
 * @param {Object} apiData - API response data with formatted_date or date
 * @param {dayjs.Dayjs} fallbackDate - Fallback date if API data is not available
 * @param {string} tz - The timezone to use (defaults to browser timezone)
 * @returns {string} The formatted date string
 */
export const getFormattedDate = (apiData, fallbackDate, tz = getBrowserTimezone()) => {
  if (apiData?.formatted_date) {
    return apiData.formatted_date;
  }
  if (apiData?.date) {
    return formatInTimezone(apiData.date, 'MMM DD, YYYY', tz);
  }
  if (fallbackDate) {
    return formatInTimezone(fallbackDate, 'MMM DD, YYYY', tz);
  }
  return formatInTimezone(dayjs(), 'MMM DD, YYYY', tz);
};

export const getBiddingStrategyLabel = (value) => {
  const option = BIDDING_STRATEGY_OPTIONS.find((opt) => opt.value === value);
  return option?.label || value;
};

export const getOptimizationStrategyLabel = (value) => {
  const option = OPTIMIZATION_STRATEGY_OPTIONS.find((opt) => opt.value === value);
  return option?.label || value;
};
